import type { WidgetResponse } from '@content-scheduler/shared/render';
import { useEffect, useState, type ReactNode } from 'react';
import { TemplateView } from '../templates/TemplateView';
import { fetchWidget, registerWidget } from './api';
import { Placeholder } from './Placeholder';
import { useAutoHeight } from './useAutoHeight';
import type { WidgetEnv } from './wix';

type State =
    { status: 'loading' } | { status: 'ready'; data: WidgetResponse } | { status: 'error' };

// setTimeout overflows past ~24.8 days; re-check at least that often.
const MAX_DELAY_MS = 2 ** 31 - 1;

function useWidgetContent(env: WidgetEnv): State {
    const [state, setState] = useState<State>({ status: 'loading' });
    const [refresh, setRefresh] = useState(0);
    const { instance, compId, originCompId, mode } = env;

    useEffect(() => {
        if (!instance || !compId) return;
        const controller = new AbortController();
        let timer = 0;
        const ready = { instance, compId, originCompId, mode, signal: controller.signal };

        (async () => {
            try {
                if (mode === 'editor' && refresh === 0) {
                    // Setup failures shouldn't hide content that already exists.
                    await registerWidget(ready).catch((err) => console.warn(err));
                }
                const data = await fetchWidget(ready);
                if (controller.signal.aborted) return;
                setState({ status: 'ready', data });
                if (data.validUntil) {
                    // Re-fetch just after the boundary, when the next item takes over.
                    const delay = new Date(data.validUntil).getTime() - Date.now() + 1000;
                    timer = window.setTimeout(
                        () => setRefresh((n) => n + 1),
                        Math.min(Math.max(delay, 1000), MAX_DELAY_MS),
                    );
                }
            } catch (err) {
                if (controller.signal.aborted) return;
                console.error(err);
                setState({ status: 'error' });
            }
        })();

        return () => {
            controller.abort();
            window.clearTimeout(timer);
        };
    }, [instance, compId, originCompId, mode, refresh]);

    return state;
}

export function WidgetApp({ env }: { env: WidgetEnv }) {
    const ref = useAutoHeight<HTMLDivElement>();
    const state = useWidgetContent(env);
    const inEditor = env.mode === 'editor';

    let view: ReactNode = null;
    if (!env.instance || !env.compId) {
        view = inEditor || import.meta.env.DEV ? <Placeholder kind="unconfigured" /> : null;
    } else if (state.status === 'error') {
        view = inEditor ? <Placeholder kind="error" /> : null;
    } else if (state.status === 'ready') {
        const { content, reason } = state.data;
        view = content ? (
            <TemplateView
                doc={content.doc}
                ctx={{
                    mode: env.mode,
                    contentKey: `${content.id}@${content.publishedAt}`,
                    schedule: content.schedule,
                }}
            />
        ) : inEditor && reason ? (
            <Placeholder kind={reason} />
        ) : null;
    }

    return (
        <div ref={ref} data-widget-state={state.status}>
            {view}
        </div>
    );
}
